// masters.js
import { defineStore } from "pinia";
import { useI18n } from "vue-i18n";
import WWSS from "~/utils/wwss";
import { useWarningStore } from "./globalMessage";
import { useCalendar } from "~/store/calendar";
import { useUserStore } from "~/store/auth";
import { useGlobalScheduling } from "~/store/globalScheduling";
import { useLocationSelector } from "~/store/locationSelector";

import { getWeek, changeFormatMDY } from "~/composables/dates";
import { beforeUnloadHandler } from "~/composables/general";
import { woTypesColors } from "~/composables/workorders";
import { useSortMates } from "~/composables/masters";

export const useMasters = defineStore("masters", {
  state: () => ({
    modalShow: false,
    globalLoading: true,
    machines: {
      changed_at: null,
      list: [],
      totalList: [],
      sites: {},
      assets: {},
      locations: {
        list: {}, //Filtrado
        map: {}, //Peticiones
      },
      tworkplaces: {
        list: [],
      },
      countries: {},
      orgids: {},
      parqueswer: {},
    },
    holidays: {
      changed_at: null,
      list: [],
      holidaysPerWeek: {},
    },
    mates: {
      changed_at: null,
      list: [],
      uniquePersonGroups: [],
    },
    crews: {
      changed_at: null,
      list: [],
      map: {},
      mapId: {},
      mapAMCREW: {},
      types: [],
      types_changed_at: null,
    },
    subcontractors: {
      list: [],
      changed_at: null,
    },
    personGroups: {
      changed_at: null,
      list: [],
      uniquePersonGroups: [],
    },
    workTypes: {
      list: [],
      map: {},
      changed_at: null,
    },
    locTypes: {
      list: [],
      changed_at: null,
    },
    technologies: {
      list: [],
      changed_at: null,
    },
    logTypes: {
      list: [],
      changed_at: null,
    },
    reliabilityDegrees: {
      list: [],
      map: {},
      changed_at: null,
    },
    tdocTasks: {
      list: [],
      changed_at: null,
    },
  }),
  actions: {
    setMachines(data) {
      this.machines.changed_at = parseDateAsUTC().getTime();
      this.machines.totalList = data;
      const machines_data = JSON.parse(JSON.stringify(data));
      let machines = {
        list: [],
        totalList: [],
        sites: {},
        assets: {},
        locations: {
          list: {}, //Filtrado
          map: {}, //Peticiones
        },
        tworkplaces: {
          list: [],
        },
        countries: {},
        orgids: {},
        parqueswer: {},
      };

      machines.list = machines_data.filter((machine) => {
        return machine.VISUALIZA == "1";
      });
      machines.tworkplaces.list = Array.from(
        new Set(
          machines.list
            .map((machine) => machine.ID_TWORKPLACE)
            .filter((id) => id !== null && id !== undefined && id !== "")
            .map(String)
        )
      );

      for (const machine of machines.list) {
        // Sites
        machines.sites[machine.SITEID] = {
          DESLOCATION: machine.DESLOCATION,
          SITEDESC: machine.SITEDESC,
          ORGID: machine.ORGID,
          COUNTRY: machine.COUNTRY,
        };

        // Countries
        if (!machines.countries[machine.COUNTRY]) {
          machines.countries[machine.COUNTRY] = {
            COUNTRYDESC_ES: machine.COUNTRYDESC_ES,
            COUNTRYDESC_EN: machine.COUNTRYDESC_EN,
            sites: [],
          };
        }

        // Añadimos el SITEID al país si no está añadido
        if (
          !machines.countries[machine.COUNTRY].sites.includes(machine.SITEID)
        ) {
          machines.countries[machine.COUNTRY].sites.push(machine.SITEID);
        }

        // ORGIDs
        if (!machines.orgids[machine.ORGID]) {
          machines.orgids[machine.ORGID] = {
            ORGDESC_ES: ORGANIZATIONS[machine.ORGID]?.ORGDESC_ES ?? "OTROS",
            ORGDESC_EN: ORGANIZATIONS[machine.ORGID]?.ORGDESC_EN ?? "OTHERS",
            countries: [machine.COUNTRY],
            sites: [],
          };
        }

        // Añadimos el país al ORGID si no está añadido
        if (
          !machines.orgids[machine.ORGID].countries.includes(machine.COUNTRY)
        ) {
          machines.orgids[machine.ORGID].countries.push(machine.COUNTRY);
        }

        // Añadimos el SITEID al ORGID si no está añadido
        if (!machines.orgids[machine.ORGID].sites.includes(machine.SITEID)) {
          machines.orgids[machine.ORGID].sites.push(machine.SITEID);
        }

        // Locations
        // TODO: Revisar UID de ASSETS y LOCATIONS con SITEID
        let location = `${machine.SITEID}-${machine.LOCATION}`;

        if (!machines.locations.map[location]) {
          machines.locations.map[location] = {
            DESLOCATION: machine.DESLOCATION, //input
            DESWINDFARM: machine.DESWINDFARM, //output del algoritmo
          };
        }

        if (!machines.locations.list[location]) {
          machines.locations.list[location] = {
            DESLOCATION: machine.DESLOCATION, //input
          };
        }

        // Parquewers
        if (!machines.parqueswer[machine.PARQUEWER]) {
          const parquewer = `${machine.ORGID}-${machine.SITEID}-${
            machine.LOCATION.split(".")[0]
          }`;
          machines.parqueswer[machine.PARQUEWER] = parquewer;
        }

        // Assets
        const asset = `${machine.SITEID}-${machine.LOCATION.split(".")[0]}`;
        if (!machines.assets[asset]) {
          machines.assets[asset] = machine.DESWINDFARM;
        }
      }
      this.machines.list = machines.list;
      this.machines.sites = machines.sites;
      this.machines.assets = machines.assets;
      this.machines.countries = machines.countries;
      this.machines.locations = machines.locations;
      this.machines.tworkplaces = machines.tworkplaces;
      this.machines.orgids = machines.orgids;
      this.machines.parqueswer = machines.parqueswer;
      this.machines.changed_at = parseDateAsUTC().getTime();
    },
    setHolidays(data) {
      this.holidays.changed_at = parseDateAsUTC().getTime();
      this.holidays.list = data;
    },
    setHolidaysPerWeek(data) {
      // const nuxtApp = useNuxtApp();
      // const locale = nuxtApp?.$i18n?.locale?.value?.toUpperCase() ?? "ES";

      const listSiteId = Object.keys(this.machines.sites);
      const listOrgIds = Object.keys(this.machines.orgids);
      const listCountryId = Object.keys(this.machines.countries);
      for (const element of data) {
        if (
          listSiteId.includes(element.CALENDARIO)
          ||
          (listCountryId.includes(element.CALENDARIO) && listOrgIds.concat("ACCIONA").includes(element.ORGID))
        ) {
          const week = getWeek(
            parseDateAsUTC(changeFormatMDY(element.FESTIVO))
          );
          this.holidays.holidaysPerWeek[week] ??= [];
          this.holidays.holidaysPerWeek[week].push(element);
        }
      }

      const calendar = useCalendar();
      calendar.setHolidaysWeek();
    },
    // setMates(data) {
    //   this.mates.changed_at = parseDateAsUTC().getTime();
    //   const mates = data.map((mate) => {
    //     return {
    //       ...mate,
    //       SEARCH: `${mate.LABORID} ${
    //         mate.DEFAULTPERSONGROUP ? " · " + mate.DEFAULTPERSONGROUP : ""
    //       } · ${mate.FIRSTNAME} ${mate?.LASTNAME || ""}`,
    //     };
    //   });

    //   this.mates.list = mates;
    // },
    setMates(data) {
      const { sortMates } = useSortMates();
      const loggedGroup = useUserStore().getProfile?.DEFAULTPERSONGROUP || null;
      this.mates.changed_at = parseDateAsUTC().getTime();

      const matesMap = data.map((usuario) => {
        // 1. Convertimos el string "019,1021" en un array ["019", "1021"]
        const ids = usuario.PERSONGROUPS ? usuario.PERSONGROUPS.split(",") : [];

        // 2. Creamos el objeto de mapeo para este usuario
        const mapaDescripciones = Object.fromEntries(
          this.personGroups.list.map((g) => [g.PERSONGROUP, g.DESCRIPTION])
        );
        const gruposDetalle = {};
        ids.forEach((id) => {
          // Limpiamos espacios por si acaso con .trim()
          const cleanId = id.trim();
          gruposDetalle[cleanId] = mapaDescripciones[cleanId] || cleanId;
        });

        // 3. Devolvemos el usuario con el nuevo campo transformado
        return {
          ...usuario,
          PERSONGROUPS_OBJ: gruposDetalle, // Nuevo campo con el objeto
        };
      });

      this.mates.list = sortMates(matesMap, loggedGroup);

      // Aprovechar para encontrar personGroups a partir de los mates
      this.mates.uniquePersonGroups = Array.from(
        new Set(this.mates.list.map((mate) => mate.DEFAULTPERSONGROUP))
      );
    },

    setCrews(data) {
      this.crews.changed_at = parseDateAsUTC().getTime();

      data = sortElements(data, "AMCREW");

      let modifiedData = data.map((item) => {
        const siteIds = item.SITEID_AMCREWSITEIDID.split(",").map(
          (id) => id.split("_")[0]
        );
        item.SITEID = Array.from(new Set(siteIds));
        return item; // Retorna el objeto modificado
      });

      this.crews.mapId = modifiedData.reduce((obj, item) => {
        obj[item.AMCREWID] = item;
        return obj;
      }, {});

      this.crews.mapAMCREW = modifiedData.reduce((obj, item) => {
        obj[item.AMCREW] = item;
        return obj;
      }, {});

      this.crews.list = modifiedData;
    },
    setCrewTypes(data) {
      this.crews.types = sortElements(data, "AMCREWTYPE");
      this.crews.types_changed_at = parseDateAsUTC().getTime();
    },
    setSubcontractors(data) {
      this.subcontractors.list = sortElements(data, "VALUE");
      this.subcontractors.changed_at = parseDateAsUTC().getTime();
    },
    setPersonGroups(data) {
      this.personGroups.list = sortElements(data, "PERSONGROUP");
      this.personGroups.changed_at = parseDateAsUTC().getTime();

      // Aprovechar para encontrar personGroups únicos
      this.personGroups.uniquePersonGroups = Array.from(
        new Set(this.personGroups.list.map((pg) => pg.PERSONGROUP))
      );
    },
    setWorkTypes(data) {
      const workTypes_list = [];
      const workTypes = {};
      for (let i = 0; i < data.length; i++) {
        const { WORKTYPE, DESCRIPTION_EN, DESCRIPTION_ES, ORGID } = data[i];

        if (!workTypes[WORKTYPE]) {
          workTypes_list.push(data[i]);
          workTypes[WORKTYPE] = {
            WORKTYPE,
            DESCRIPTION_EN,
            DESCRIPTION_ES,
            ORGIDs: [],
            COLOR: woTypesColors[WORKTYPE] || woTypesColors["DEFAULT"], // Color por defecto si no se encuentra en woTypesColors
          };
        }

        if (!workTypes[WORKTYPE].ORGIDs.includes(ORGID)) {
          workTypes[WORKTYPE].ORGIDs.push(ORGID);
        }
      }

      this.workTypes.list = sortElements(workTypes_list, "WORKTYPE");
      this.workTypes.map = workTypes;
      this.workTypes.changed_at = parseDateAsUTC().getTime();
    },

    setLocTypes(data) {
      this.locTypes.list = sortElements(
        removeDuplicates(data, "VALUE"),
        "VALUE"
      );
      this.locTypes.changed_at = parseDateAsUTC().getTime();
    },

    setTechnologies(data) {
      this.technologies.list = sortElements(data, "VALUE");
      this.technologies.changed_at = parseDateAsUTC().getTime();
    },
    setlogTypes(data) {
      this.logTypes.list = sortElements(data, "VALUE");
      this.logTypes.changed_at = new Date().getTime();
    },
    setReliabilityDegrees(data) {
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.result)
        ? data.result
        : [];

      this.reliabilityDegrees.list = list;
      this.reliabilityDegrees.map = list.reduce((map, item) => {
        const value = item.RELIABILITYDEGREE ?? item.VALUE;
        if (value !== null && value !== undefined) {
          map[String(value)] = item;
        }
        return map;
      }, {});
      this.reliabilityDegrees.changed_at = parseDateAsUTC().getTime();
    },
    deleteCrew(crewId) {
      const filtered_crews = this.crews.list.filter(
        (crew) => crew.AMCREWID !== crewId
      );
      this.setCrews(filtered_crews);
    },
    setTdocTasks(data) {
      this.tdocTasks.list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.result)
        ? data.result
        : [];
      this.tdocTasks.changed_at = parseDateAsUTC().getTime();
    },
    addCrew(data) {
      this.crews.list.push(data);
      this.setCrews(this.crews.list);
    },
    async fetchAllMachines() {
      const globalMessage = useWarningStore();
      const userStore = useUserStore();
      const nuxtApp = useNuxtApp();

      const data = await fetchPostData(WWSS.user.machines.list, {
        item: userStore.login?.CodOperarioMaximo,
      }).catch((reason) => {
        globalMessage.showGenericErrorMessage(
          nuxtApp.$i18n.t("errors.messages.backend.machines")
        );
      });

      if (data && data.error) {
        globalMessage.showGenericErrorMessage(
          nuxtApp.$i18n.t("errors.messages.backend.machines")
        );
        return [];
      } else {
        this.setMachines(data);

        // Si el usuario no tiene ubicaciones seleccionadas (VISUALIZA=1), abrir el selector.
        // Evita que el usuario vea el warning `backend.noSites` al intentar cargar datos.
        const sitesCount = Object.keys(this.machines?.sites || {}).length;
        if (import.meta.client && sitesCount === 0 && (this.machines.totalList || []).length) {
          const locationSelector = useLocationSelector();
          locationSelector.openMandatory();
        }

        return this.machines.list;
      }
    },
    async fetchHolidays(params = { country: "", siteid: "" }) {
      const globalMessage = useWarningStore();
      const nuxtApp = useNuxtApp();

      const data = await fetchPostData(WWSS.master.holidays, {}).catch(
        (reason) => {
          globalMessage.showGenericErrorMessage(
            nuxtApp.$i18n.t("errors.messages.backend.holidays")
          );
        }
      );

      if (data && data.error) {
        globalMessage.showGenericErrorMessage(
          nuxtApp.$i18n.t("errors.messages.backend.holidays")
        );
        return [];
      } else {
        this.setHolidays(data);
        this.setHolidaysPerWeek(data);
        return this.holidays.list;
      }
    },
    async fetchAllMates() {
      const globalMessage = useWarningStore();
      const nuxtApp = useNuxtApp();
      const userStore = useUserStore();

      const data = await fetchPostData(WWSS.user.mates, {
        item: userStore?.login?.CodOperarioMaximo,
      }).catch((reason) => {
        globalMessage.showGenericErrorMessage(
          nuxtApp.$i18n.t("errors.messages.backend.mates")
        );
      });

      if (data && data.error) {
        globalMessage.showGenericErrorMessage(
          nuxtApp.$i18n.t("errors.messages.backend.mates")
        );
        return [];
      } else {
        this.setMates(data);
        return this.mates.list;
      }
    },
    async fetchAllCrews() {
      const globalMessage = useWarningStore();
      const nuxtApp = useNuxtApp();
      const userStore = useUserStore();

      const sitesCount = Object.keys(this.machines?.sites || {}).length;
      const totalMachines = (this.machines?.totalList || []).length;
      if (sitesCount === 0) {
        if (import.meta.client && totalMachines > 0) {
          const locationSelector = useLocationSelector();
          locationSelector.openMandatory();
        } else {
          globalMessage.showGenericWarningMessage(
            nuxtApp.$i18n.t("errors.messages.backend.noSites")
          );
        }
        return [];
      }
      const listSitesId = Object.keys(this.machines.sites);

      let parameters = {
        siteid: listSitesId.toString(),
      };

      if (hasRole("regularSubcontractor")) {
        parameters.subcontrata = userStore.getSubcontractor;
      }

      const data = await fetchPostData(
        WWSS.user.crews.get_all_crews_siteid,
        parameters
      ).catch((reason) => {
        globalMessage.showGenericErrorMessage(
          nuxtApp.$i18n.t("errors.messages.backend.crews")
        );
      });

      if ((data && data.error) || (data && data.code)) {
        globalMessage.showGenericErrorMessage(
          nuxtApp.$i18n.t("errors.messages.backend.crews")
        );
        return [];
      } else {
        const globalScheduling = useGlobalScheduling();
        globalScheduling.setShowCrewsData(data);
        this.setCrews(data);
        return this.crews.list;
      }
    },
    setGlobalLoading(value) {
      this.globalLoading = value;
    },
    async fetchAllSubcontractors() {
      const globalMessage = useWarningStore();
      const nuxtApp = useNuxtApp();

      const data = await fetchPostData(WWSS.master.subcontractors, {}).catch(
        (reason) => {
          globalMessage.showGenericErrorMessage(
            nuxtApp.$i18n.t("errors.messages.backend.subcontractors")
          );
        }
      );

      if (data && data.error) {
        globalMessage.showGenericErrorMessage(
          nuxtApp.$i18n.t("errors.messages.backend.subcontractors")
        );
        return [];
      } else {
        this.setSubcontractors(sortElements(data, "DESCRIPTION_ES"));
        return this.subcontractors.list;
      }
    },
    async fetchAllCrewTypes() {
      const globalMessage = useWarningStore();
      const nuxtApp = useNuxtApp();

      const data = await fetchPostData(WWSS.master.crews.types, {}).catch(
        (reason) => {
          globalMessage.showGenericErrorMessage(
            nuxtApp.$i18n.t("errors.messages.backend.crewTypes")
          );
        }
      );

      if (data && data.error) {
        globalMessage.showGenericErrorMessage(
          nuxtApp.$i18n.t("errors.messages.backend.crewTypes")
        );
        return [];
      } else {
        this.setCrewTypes(sortElements(data, "DESCRIPTION"));
        return this.crews.types;
      }
    },
    async fetchAllPersonGroups() {
      const globalMessage = useWarningStore();
      const userStore = useUserStore();
      const nuxtApp = useNuxtApp();

      const data = await fetchPostData(WWSS.master.personGroupsAccess, {
        item: userStore?.login?.CodOperarioMaximo,
      }).catch((reason) => {
        globalMessage.showGenericErrorMessage(
          nuxtApp.$i18n.t("errors.messages.backend.personGroupsAccess")
        );
      });

      if (data && data.error) {
        globalMessage.showGenericErrorMessage(
          nuxtApp.$i18n.t("errors.messages.backend.personGroupsAccess")
        );
        return [];
      } else {
        this.setPersonGroups(data);
        return this.personGroups.list;
      }
    },
    async fetchAllWorkTypes() {
      const globalMessage = useWarningStore();
      const nuxtApp = useNuxtApp();

      const data = await fetchPostData(WWSS.master.workTypes, {}).catch(
        (reason) => {
          globalMessage.showGenericErrorMessage(
            nuxtApp.$i18n.t("errors.messages.backend.workTypes")
          );
        }
      );

      if (data && data.error) {
        globalMessage.showGenericErrorMessage(
          nuxtApp.$i18n.t("errors.messages.backend.workTypes")
        );
        return [];
      } else {
        this.setWorkTypes(data);
        return this.workTypes.list;
      }
    },
    async fetchAllLocTypes() {
      const globalMessage = useWarningStore();
      const nuxtApp = useNuxtApp();

      const data = await fetchPostData(WWSS.master.locTypes, {}).catch(
        (reason) => {
          globalMessage.showGenericErrorMessage(
            nuxtApp.$i18n.t("errors.messages.backend.locTypes")
          );
        }
      );

      if (data && data.error) {
        globalMessage.showGenericErrorMessage(
          nuxtApp.$i18n.t("errors.messages.backend.locTypes")
        );
        return [];
      } else {
        this.setLocTypes(data);
        return this.locTypes.list;
      }
    },
    async fetchAllTechnologies() {
      const globalMessage = useWarningStore();
      const nuxtApp = useNuxtApp();

      const data = await fetchPostData(WWSS.master.technologies, {}).catch(
        (reason) => {
          globalMessage.showGenericErrorMessage(
            nuxtApp.$i18n.t("errors.messages.backend.technologies")
          );
        }
      );

      if (data && data.error) {
        globalMessage.showGenericErrorMessage(
          nuxtApp.$i18n.t("errors.messages.backend.technologies")
        );
        return [];
      } else {
        this.setTechnologies(data);
        return this.technologies.list;
      }
    },
    async fetchAllLogTypes() {
      const globalMessage = useWarningStore();
      const nuxtApp = useNuxtApp();

      const data = await fetchPostData(WWSS.master.logTypes, {}).catch(
        (reason) => {
          globalMessage.showGenericErrorMessage(
            nuxtApp.$i18n.t("errors.messages.backend.logTypes")
          );
        }
      );

      if (data && data.error) {
        globalMessage.showGenericErrorMessage(
          nuxtApp.$i18n.t("errors.messages.backend.logTypes")
        );
        return [];
      } else {
        this.setlogTypes(data);
        return this.logTypes.list;
      }
    },
    async fetchReliabilityDegrees() {
      const globalMessage = useWarningStore();
      const nuxtApp = useNuxtApp();

      const data = await fetchPostData(WWSS.master.reliabilitydegree, {}).catch(
        (reason) => {
          globalMessage.showGenericErrorMessage(
            nuxtApp.$i18n.t("errors.messages.backend.reliabilityDegrees")
          );
        }
      );

      if (data && data.error) {
        globalMessage.showGenericErrorMessage(
          nuxtApp.$i18n.t("errors.messages.backend.reliabilityDegrees")
        );
        return [];
      }

      this.setReliabilityDegrees(data);
      return this.reliabilityDegrees.list;
    },
    async fetchTdocTasks() {
      const globalMessage = useWarningStore();
      const nuxtApp = useNuxtApp();

      const data = await fetchPostData(WWSS.master.tdoctasks, {}).catch(
        (reason) => {
          globalMessage.showGenericErrorMessage(
            nuxtApp.$i18n.t("errors.messages.backend.tdocTasks")
          );
        }
      );

      if (data && data.error) {
        globalMessage.showGenericErrorMessage(
          nuxtApp.$i18n.t("errors.messages.backend.tdocTasks")
        );
        return [];
      }

      this.setTdocTasks(data);
      return this.tdocTasks.list;
    },
    setOnCloseConfirm(mode) {
      if (mode) {
        window.addEventListener("beforeunload", beforeUnloadHandler);
      } else {
        window.removeEventListener("beforeunload", beforeUnloadHandler);
      }
    },
    setShowModal(value) {
      this.modalShow = value;
    },
  },
  getters: {
    getAllMachines: (state) => state.machines.list,
    getMachinesChangedAt: (state) => state.machines.changed_at,
    areMachinesUpdated(state) {
      if (state.machines.changed_at === null) {
        return false;
      }
      const timeDifference =
        parseDateAsUTC().getTime() - state.machines.changed_at;
      return timeDifference < 10800000; // 3 horas en milisegundos 3 * 60 * 60 * 1000
    },
    getSiteName(state) {
      return (siteid) => {
        return state.machines.sites[siteid]?.SITEDESC || "";
      };
    },
    getLocationCodeAndName(state) {
      return (locationId) => {
        const location = state.machines.locations.map[locationId];
        const cod_location = locationId.split("-")[1] + " · ";
        return cod_location + location?.DESLOCATION || ""; // Retorna DESLOCATION si se encuentra, de lo contrario null
      };
    },
    getLocationName(state) {
      return (locationId) => {
        const location = state.machines.locations.map[locationId];
        return location?.DESLOCATION || locationId; // Retorna DESLOCATION si se encuentra, de lo contrario null
      };
    },
    getHolidays: (state) => state.holidays.list,
    getHolidaysBySiteCalendar: (state) => { // Ejemplo: siteKey = "P090" -> CALENDARIO "P090" o "CL"
      return (siteKey) => {
        if (!siteKey) return [];

        const site = state.machines?.sites?.[siteKey];
        const country = site?.COUNTRY;

        return (state.holidays.list ?? []).filter((holiday) => {
          return (
            holiday?.CALENDARIO === siteKey ||
            (country && holiday?.CALENDARIO === country)
          );
        });
      };
    },
    getHolidaysPerWeek: (state) => state.holidays.holidaysPerWeek,
    getHolidaysChangedAt: (state) => state.holidays.changed_at,
    areHolidaysUpdated(state) {
      if (state.holidays.changed_at === null) {
        return false;
      }
      const timeDifference =
        parseDateAsUTC().getTime() - state.holidays.changed_at;
      return timeDifference < 10800000; // 3 horas en milisegundos 3 * 60 * 60 * 1000
    },

    getAllMates: (state) => state.mates.list,
    getAllSortMates: (state) => {
      return state.mates.list;
    },
    getMatesChangedAt: (state) => state.mates.changed_at,
    areMatesUpdated(state) {
      if (state.mates.changed_at === null) {
        return false;
      }
      const timeDifference =
        parseDateAsUTC().getTime() - state.mates.changed_at;
      return timeDifference < 10800000; // 3 horas en milisegundos 3 * 60 * 60 * 1000
    },
    areSubcontractorsUpdated(state) {
      if (state.subcontractors.changed_at === null) {
        return false;
      }
      const timeDifference =
        parseDateAsUTC().getTime() - state.subcontractors.changed_at;
      return timeDifference < 10800000;
    },
    getAllSubcontractors: (state) => state.subcontractors.list,
    areCrewTypesUpdated(state) {
      if (state.crews.types_changed_at === null) {
        return false;
      }
      const timeDifference =
        parseDateAsUTC().getTime() - state.crews.types_changed_at;
      return timeDifference < 10800000;
    },
    getAllCrewTypes: (state) => state.crews.types,
    getAllCrews: (state) => state.crews.list,
    getAllCrewsId: (state) => state.crews.mapId,
    getAllCrewsAMCREW: (state) => state.crews.mapAMCREW,
    areCrewsUpdated: (state) => {
      if (state.crews.changed_at === null) {
        return false;
      }
      const timeDifference =
        parseDateAsUTC().getTime() - state.crews.changed_at;
      return timeDifference < 10800000;
    },
    getCrewDescription: (state) => {
      const nuxtApp = useNuxtApp();
      return (crewid) => {
        if (crewid == CREW_NULL)
          return nuxtApp.$i18n.t("scheduling.withoutCrew");
        return state.crews.mapAMCREW[crewid]?.DESCRIPTION;
      };
    },
    getMatePersonGroup: (state) => {
      return (laborcode) => {
        if (!laborcode) return null;
        const mate = state.mates.list.find((pg) => pg.LABORID === laborcode);
        return mate ? mate.DEFAULTPERSONGROUP : null;
      };
    },
    getPersonGroupTitle: (state) => {
      return (personGroup) => {
        if (!personGroup) return null;
        const group = state.personGroups.list.find(
          (pg) => pg.PERSONGROUP === personGroup
        );

        return group ? `${group.PERSONGROUP} · ${group.DESCRIPTION || ""}` : "";
      };
    },
    getLogtypeTitle: (state) => {
      // const { locale } = useI18n(); // idioma actual de nuxt-i18n
      const { $i18n } = useNuxtApp();

      return (logtype) => {
        if (!logtype) return null;

        const type = state.logTypes.list.find((lt) => lt.VALUE === logtype);
        if (!type) return "";

        const key = `DESCRIPTION_${$i18n.locale.value.toUpperCase()}`;
        return type[key] || type.DESCRIPTION || "";
      };
    },
    getAllWorkTypes: (state) => state.workTypes.list,
    getWorkTypesMap: (state) => state.workTypes.map,
    getReliabilityDegree: (state) => (value) =>
      state.reliabilityDegrees.map[String(value)],
    getReliabilityDegreeDescription: (state) => (value) => {
      const item = state.reliabilityDegrees.map[String(value)];
      if (!item) return "";

      const locale = useNuxtApp().$i18n.locale.value.toUpperCase();
      return (
        item[`DESCRIPTION_${locale}`] ||
        item.DESCRIPTION ||
        item.DESCRIPTION_ES ||
        item.DESCRIPTION_EN ||
        ""
      );
    },
    getSites: (state) => {
      return Object.keys(state.machines?.sites)?.join(",") ?? "";
    },
    areWorkTypesUpdated(state) {
      if (state.workTypes.changed_at === null) {
        return false;
      }
      const timeDifference =
        parseDateAsUTC().getTime() - state.workTypes.changed_at;
      return timeDifference < 10800000;
    },
    areLocTypesUpdated(state) {
      if (state.locTypes.changed_at === null) {
        return false;
      }
      const timeDifference =
        parseDateAsUTC().getTime() - state.locTypes.changed_at;
      return timeDifference < 10800000;
    },
    areTechnologiesUpdated(state) {
      if (state.technologies.changed_at === null) {
        return false;
      }
      const timeDifference =
        parseDateAsUTC().getTime() - state.technologies.changed_at;
      return timeDifference < 10800000;
    },
    areReliabilityDegreesUpdated(state) {
      if (state.reliabilityDegrees.changed_at === null) {
        return false;
      }
      const timeDifference =
        parseDateAsUTC().getTime() - state.reliabilityDegrees.changed_at;
      return timeDifference < 10800000;
    },
    areTdocTasksUpdated(state) {
      if (state.tdocTasks.changed_at === null) {
        return false;
      }
      const timeDifference =
        parseDateAsUTC().getTime() - state.tdocTasks.changed_at;
      return timeDifference < 10800000;
    },
    arePersonGroupsUpdated(state) {
      if (state.personGroups.changed_at === null) {
        return false;
      }
      const timeDifference =
        parseDateAsUTC().getTime() - state.personGroups.changed_at;
      return timeDifference < 10800000;
    },
    isLocationWlPrNeeded(state) {
      return (otLocationId) => {
        let siteId = otLocationId.split("-")[0]; // Ej. 002
        let locationId = otLocationId.split("-")[1]; // Ej. 0012.039

        // Encontrar la machine que corresponde
        const machine = state.machines.list.find(
          (m) =>
            m.SITEID === siteId &&
            (m.LOCATION === locationId || m.LOCATION === locationId)
        );

        if (machine && machine.REC_PREV === "Y") {
          return true;
        }

        return false;
      };
    },
  },
});
